/**
 * Network State Service
 * 
 * Monitors network connectivity and manages offline/online state.
 * Queues requests when offline and processes them when online.
 * 
 * Issue: #202
 */

import { Injectable, OnDestroy } from '@angular/core'
import { BehaviorSubject, Observable, interval, Subscription } from 'rxjs'
import { distinctUntilChanged } from 'rxjs/operators'
import { HttpRequest } from '@angular/common/http'

import { RequestQueueService } from '@/services/request-queue.service'
import { log } from '@/util/logger'

/**
 * Service for monitoring network connectivity and managing offline state.
 */
@Injectable({ providedIn: 'root' })
export class NetworkStateService implements OnDestroy {
  private _isOnline = new BehaviorSubject<boolean>(navigator.onLine)
  private _connectivityCheckInterval = 30000 // 30 seconds
  private _connectivityCheckSubscription: Subscription | null = null
  private _pendingRequests: HttpRequest<any>[] = []
  private readonly _maxQueueSize = 20 // Prevent unbounded queue growth

  public readonly isOnline$ = this._isOnline.asObservable().pipe(
    distinctUntilChanged()
  )

  constructor(private _requestQueue: RequestQueueService) {
    // Listen to browser online/offline events
    window.addEventListener('online', () => this._handleOnline())
    window.addEventListener('offline', () => this._handleOffline())

    // Start periodic connectivity checks
    this._startConnectivityChecks()

    // Log initial state
    log(
      `[NetworkState] Initial network state: ${navigator.onLine ? 'online' : 'offline'}`
    )
  }

  /**
   * Get current online status synchronously
   */
  get isOnline(): boolean {
    return this._isOnline.value
  }

  /**
   * Start periodic connectivity checks
   */
  private _startConnectivityChecks(): void {
    this._connectivityCheckSubscription = interval(
      this._connectivityCheckInterval
    ).subscribe(() => {
      this._checkConnectivity()
    })
  }

  /**
   * Check network connectivity
   */
  private _checkConnectivity(): void {
    // Use a lightweight HEAD request to check connectivity
    // Use a small timeout to avoid blocking
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 3000)

    fetch('/api/health', {
      method: 'HEAD',
      cache: 'no-cache',
      signal: controller.signal
    })
      .then(() => {
        clearTimeout(timeoutId)
        if (!this._isOnline.value) {
          this._handleOnline()
        }
      })
      .catch(() => {
        clearTimeout(timeoutId)
        if (this._isOnline.value) {
          this._handleOffline()
        }
      })
  }

  /**
   * Handle network coming online
   */
  private _handleOnline(): void {
    log('[NetworkState] Network online')
    this._isOnline.next(true)
    this._processPendingRequests()
  }

  /**
   * Handle network going offline
   */
  private _handleOffline(): void {
    log('[NetworkState] Network offline')
    this._isOnline.next(false)
  }

  /**
   * Queue a request to be processed when network comes online.
   * Deduplicates by URL+method to prevent queue bloat from repeated polling.
   */
  queueRequestWhenOffline(request: HttpRequest<any>): void {
    if (!this._isOnline.value) {
      // Check if this URL+method combo is already queued
      const isDuplicate = this._pendingRequests.some(
        r => r.url === request.url && r.method === request.method
      )

      if (isDuplicate) {
        log(
          `[NetworkState] Request already queued, skipping duplicate: ${request.method} ${request.url}`
        )
        return
      }

      // Enforce max queue size
      if (this._pendingRequests.length >= this._maxQueueSize) {
        log(
          `[NetworkState] Queue full (${this._maxQueueSize}), dropping request: ${request.method} ${request.url}`
        )
        return
      }

      this._pendingRequests.push(request)
      log(
        `[NetworkState] Queued request for when online: ${request.url} (${this._pendingRequests.length} queued)`
      )
    }
  }

  /**
   * Process pending requests when network comes online
   */
  private _processPendingRequests(): void {
    if (this._pendingRequests.length === 0) {
      return
    }

    log(
      `[NetworkState] Processing ${this._pendingRequests.length} pending requests`
    )

    const requests = [...this._pendingRequests]
    this._pendingRequests = []

    // Queue all pending requests
    requests.forEach(request => {
      this._requestQueue.queueRequest(request)
    })
  }

  /**
   * Get number of pending requests
   */
  get pendingRequestCount(): number {
    return this._pendingRequests.length
  }

  /**
   * Clear pending requests
   */
  clearPendingRequests(): void {
    log(
      `[NetworkState] Clearing ${this._pendingRequests.length} pending requests`
    )
    this._pendingRequests = []
  }

  ngOnDestroy(): void {
    if (this._connectivityCheckSubscription) {
      this._connectivityCheckSubscription.unsubscribe()
    }
    window.removeEventListener('online', () => this._handleOnline())
    window.removeEventListener('offline', () => this._handleOffline())
  }
}
